import React from 'react';
import MilestoneBar from './shared/MilestoneBar';
import CarePeriodRing from './shared/CarePeriodRing';
import CarePeriodStatusBadge from './shared/CarePeriodStatusBadge';
import TrackBadge from './shared/TrackBadge';
import WithdrawalHoldBanner from './shared/WithdrawalHoldBanner';
import { ELATION_PATIENT_BASE_URL } from '../../config/medical-constants';
import useDemoMode from '../../hooks/useDemoMode';

// The "bubble" card: full patient identity + quick actions + contact (+ Elation
// link) + care-period milestone, shown unscrolled. Once it scrolls out, the shell
// pushes the identity (+ actions) up into the pinned top bar.
export default function PatientFileHeader({ patient, carePeriod, milestoneDates, taskMilestoneDates, trackCode, subtrackId, birthdate, email, onChat, onCall, callLabel = 'Call patient', callPending = false, onSchedule, onOpenInfo, appointmentBusy, hold = null }) {
  const demoMode = useDemoMode();
  return (
    <>
      {hold && <WithdrawalHoldBanner requestedOn={hold.requestedOn} chartsLive={hold.chartsLive} onOpen={hold.onOpen} />}
      <div className="detail-header__row">
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Identity block: name, the care-period ring, then the two pills stacked at its height. */}
          <div className="pf-identity">
            {/* Email is revealed on hover of the name (.wi-tip tooltip in global.css). */}
            <h2
              className={`wi-tip header3${demoMode ? ' demo-blur' : ''}`}
              data-tip={email || undefined}
              style={{ margin: 0, textTransform: 'capitalize', cursor: email ? 'help' : undefined }}
            >
              {patient.firstname} {patient.lastname}
            </h2>
            {!patient.ehr_patient_id && <span className="detail1 text-secondary">{patient.userid}</span>}
            <CarePeriodRing carePeriod={carePeriod} />
            {(carePeriod || trackCode || subtrackId) && (
              <div className="pf-identity__pills">
                {carePeriod && <CarePeriodStatusBadge carePeriod={carePeriod} size="sm" />}
                <TrackBadge trackCode={trackCode} subtrackId={subtrackId} size="sm" />
              </div>
            )}
          </div>
          {(birthdate || patient.ehr_patient_id || onOpenInfo) && (
            <div className="pf-meta">
              {birthdate && (
                <span className="detail1 text-secondary">
                  Born {birthdate.display}{birthdate.age != null ? ` (${birthdate.age})` : ''}
                </span>
              )}
              {patient.ehr_patient_id && (
                <a
                  className="detail1"
                  href={`${ELATION_PATIENT_BASE_URL}/${patient.ehr_patient_id}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open this patient's Elation chart (ID ${patient.ehr_patient_id})`}
                  style={{ color: 'var(--wi-status-info)', fontWeight: 500, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  See in Elation
                  <span aria-hidden="true">→</span>
                </a>
              )}
              {onOpenInfo && (
                <button
                  type="button"
                  className="detail1"
                  onClick={onOpenInfo}
                  title="View this patient's contact & administrative details"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--wi-status-info)', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  See more
                  <span aria-hidden="true">›</span>
                </button>
              )}
            </div>
          )}
          <MilestoneBar carePeriod={carePeriod} milestoneDates={milestoneDates} taskMilestoneDates={taskMilestoneDates} />
        </div>

        {/* Quick actions (also surfaced in the top bar once scrolled) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <button className="btn-secondary" style={{ fontSize: 13, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 6 }} onClick={onChat}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            Chat
          </button>
          <button
            className="btn-secondary"
            style={{ fontSize: 13, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 6, opacity: callPending ? 0.5 : 1 }}
            disabled={callPending}
            aria-busy={callPending}
            title={callLabel}
            aria-label={callLabel}
            onClick={onCall}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
            Call
          </button>
          <button
            className="btn-secondary"
            style={{ fontSize: 13, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 6, opacity: !carePeriod ? 0.5 : 1 }}
            disabled={!carePeriod || appointmentBusy}
            title={!carePeriod ? 'No active care period' : 'Send the patient an Elation booking link'}
            onClick={onSchedule}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            Schedule
          </button>
        </div>
      </div>
    </>
  );
}
